/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/api/contacts.js
 *
 * Purpose: React Query hooks for the current contact ("me"), the contact list for a
 *     customer, and contact CRUD/invite/delete operations.
 *
 * Scope: Portal user/contact management, including preferences, invites, and the
 *     "current logged-in contact" query used across the app.
 *
 * Tags: customer-portal, contacts
 *
 * Overview: Exports a default `refetchMe` helper that refetches the "me" query, reused by
 *     other hook files after mutations that affect the current contact. `useMeQuery` fetches
 *     and normalizes the current contact's preferences. `useContact` fetches a single contact
 *     scoped to the active customer. `useContactsSave` upserts one or more contacts and
 *     invalidates the contacts list/detail caches, showing a success notification.
 *     `useContactPreferencesUpdate` patches contact preferences and refetches "me".
 *     `useContactList` paginates the contacts list via infinite query. `usePortalUserInvite`
 *     sends or resends a portal user invite email. `useContactCustomerDelete` removes a
 *     contact from a customer and prunes it from the cached paginated contact list.
 *
 * Exports: refetchMe (default), useMeQuery, useContact, useContactsSave,
 *     useContactPreferencesUpdate, useContactList, usePortalUserInvite, useContactCustomerDelete
 */

import { useAppStore } from 'App/store';
import qs from 'qs';
import React from 'react';
import { NotificationManager } from 'react-notifications';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { EntityType } from 'shared/constants/entityTypes';
import { axiosinstance, Contacts } from 'shared/utils/api';
import { safeJsonParse } from 'shared/utils/functions';
import { queryClient } from 'shared/utils/queryClient';
import { useEntityDisplayName } from '../misc/fields';

const ME_QUERY = 'me';
const baseUrl = '/v1/contact';

const refetchMe = () => queryClient.refetchQueries(ME_QUERY, { active: true });
export default refetchMe;

export function useMeQuery() {
  const { data = {}, ...queryObject } = useQuery(ME_QUERY, () =>
    axiosinstance.get(baseUrl).then(({ data: res }) => res.data)
  );
  return [
    {
      ...data,
      preferences: {
        ...safeJsonParse(data?.preferences),
        ...data?.contact_customer_preferences,
      },
    },
    queryObject,
  ];
}

export function useContact(id, options = {}) {
  const {
    appState: { customerId },
  } = useAppStore();
  const fetchContact = () =>
    axiosinstance
      .get(`/v1/customers/${customerId}/contacts/${id}`)
      .then(({ data: res }) => ({
        ...res.data,
        role_ids: res.portal_roles?.map(r => r.id),
      }));

  const { data = {}, ...queryResult } = useQuery(
    ['contact', id],
    fetchContact,
    options
  );

  return { data, ...queryResult };
}

export function useContactsSave() {
  const {
    appState: { customerId },
  } = useAppStore();
  const queryClient = useQueryClient();
  const [contactSingular] = useEntityDisplayName(EntityType.CONTACT);

  const mutationFn = (contacts) =>
    Contacts.save(customerId, contacts).then((res) => res?.data);

  const { mutateAsync, ...mutationObject } = useMutation({
    mutationFn,
    onSuccess: (_contacts, [payload]) => {
      const { id } = payload;
      const contactId = parseInt(id);

      queryClient.invalidateQueries('contacts', { refetchInactive: true });
      if (contactId) {
        queryClient.invalidateQueries(['contact', contactId], {
          refetchInactive: true,
        });
      }
      NotificationManager.success(`${contactSingular} successfully updated.`);
    },
    onError: (e) => {
      queryClient.invalidateQueries('contacts');
    },
  });

  return [mutateAsync, mutationObject];
}

export function useContactPreferencesUpdate() {
  const { mutate, ...mutationObject } = useMutation(
    (data) =>
      axiosinstance
        .patch(`${baseUrl}/preferences`, data)
        .then((res) => res.data),
    {
      onSuccess: refetchMe,
    }
  );
  return [mutate, mutationObject];
}

export const useContactList = (
  urlParams = { page: 1, pageSize: 20 },
  filterParams = { portal_users_only: true }
) => {
  const params = React.useMemo(() => {
    return {
      page: urlParams.page,
      page_size: urlParams.pageSize,
      ...filterParams,
    };
  }, [urlParams, filterParams]);

  const fetchContact = async ({ pageParam = params.page }) => {
    const { data } = await axiosinstance.get(`${baseUrl}s`, {
      params: {
        ...params,
        page: pageParam,
      },
      paramsSerializer: (params_1) => {
        return qs.stringify(params_1, { arrayFormat: 'repeat' });
      },
    });
    return data;
  };

  const {
    data: contacts,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery('contacts', fetchContact, {
    getNextPageParam: (lastPage, pages) =>
      lastPage.page_number === lastPage.total_pages
        ? undefined
        : lastPage.page_number + 1,
  });

  return [
    contacts,
    { fetchNextPage, isFetching, hasNextPage, isFetchingNextPage },
  ];
};

export const usePortalUserInvite = ({
  contactId,
  resend = false,
}) => {
  const {
    appState: { customerId },
  } = useAppStore();
  const { mutateAsync, ...mutationObject } = useMutation(
    () =>
      Contacts.invite(customerId, contactId, { resend }).then(
        (res) => res.data
      ),
    {
      onSuccess: (result) => {
        NotificationManager.success(
          'The invite has been sent to the portal user email.'
        );
      },
    }
  );

  return [mutateAsync, mutationObject];
};

export const useContactCustomerDelete = (contactId) => {
  const {
    appState: { customerId },
  } = useAppStore();
  const { mutateAsync, isLoading } = useMutation(
    () =>
      axiosinstance
        .delete(`/v1/customers/${customerId}/contacts/${contactId}`)
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.setQueryData(['contacts'], (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.filter((c) => c.id !== contactId),
          })),
        }));
      },
    }
  );

  return [mutateAsync, { isLoading }];
};
